import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchDashboardStats,
  fetchDashboardPipeline,
  fetchDashboardActivity,
  fetchDashboardAttention,
  type DashboardStats as ApiDashboardStats,
  type PipelineStageCount as ApiPipelineStageCount,
  type DashboardActivityItem,
  type DashboardAttentionItem,
} from '@/lib/api';
import {
  StatsCards,
  PipelineOverview,
  ActivityFeed,
  AttentionList,
  NeedsAttentionNotifications,
  type ActivityItem,
  type AttentionItem,
} from '@/components/dashboard';
import { PipelineStage } from '@/types';

export function DashboardPage() {
  const { user } = useAuth();

  // Fetch dashboard stats
  const {
    data: stats,
    isLoading: isLoadingStats,
  } = useQuery<ApiDashboardStats>({
    queryKey: ['dashboard', 'stats'],
    queryFn: fetchDashboardStats,
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch pipeline overview data
  const {
    data: pipelineData,
    isLoading: isLoadingPipeline,
  } = useQuery<ApiPipelineStageCount[]>({
    queryKey: ['dashboard', 'pipeline'],
    queryFn: fetchDashboardPipeline,
    refetchInterval: 60000,
  });

  // Fetch recent activity
  const {
    data: activityData,
    isLoading: isLoadingActivity,
  } = useQuery<DashboardActivityItem[], Error, ActivityItem[]>({
    queryKey: ['dashboard', 'activity'],
    queryFn: () => fetchDashboardActivity(15),
    refetchInterval: 30000, // Refresh every 30 seconds
    select: (data): ActivityItem[] => data.map((item) => ({
      ...item,
      entity: item.entity ? {
        ...item.entity,
      } : undefined,
    })),
  });

  // Fetch attention-needed items
  const {
    data: attentionData,
    isLoading: isLoadingAttention,
  } = useQuery<DashboardAttentionItem[], Error, AttentionItem[]>({
    queryKey: ['dashboard', 'attention'],
    queryFn: () => fetchDashboardAttention(7),
    refetchInterval: 60000,
    select: (data): AttentionItem[] => data.map((item) => ({
      ...item,
      currentStage: item.currentStage as PipelineStage,
    })),
  });

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-text-secondary mt-1">
          Here's what's happening with your recruitment pipeline today.
        </p>
      </div>

      {/* Actionable Notifications */}
      <NeedsAttentionNotifications />

      {/* Stats Cards */}
      <StatsCards stats={stats ?? null} isLoading={isLoadingStats} />

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Overview - spans 2 columns */}
        <PipelineOverview
          data={pipelineData ?? null}
          isLoading={isLoadingPipeline}
        />

        {/* Activity Feed */}
        <ActivityFeed
          activities={activityData ?? null}
          isLoading={isLoadingActivity}
        />
      </div>

      {/* Attention List - Full Width */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AttentionList
          items={attentionData ?? null}
          isLoading={isLoadingAttention}
        />
      </div>
    </div>
  );
}
